import { useSEO } from '../../hooks/useSEO';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://yourmindshift24.com';

export default function DatenschutzPage() {
  useSEO({
    title: 'Datenschutzerklärung – YourMindShift24 | DSGVO-konform',
    description: 'Datenschutzerklärung von YourMindShift24 (Future Now UG, Aschheim). Erfahre, wie wir deine Daten schützen – DSGVO-konform, verschlüsselt und anonym.',
    keywords: 'Datenschutz YourMindShift24, DSGVO, Datenschutzerklärung, Future Now UG',
    canonical: '/datenschutz',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Datenschutzerklärung – YourMindShift24',
      url: `${SITE_URL}/datenschutz`,
      description: 'DSGVO-konforme Datenschutzerklärung von YourMindShift24 – Future Now UG',
      publisher: {
        '@type': 'Organization',
        name: 'Future Now UG',
        url: SITE_URL,
      },
      inLanguage: 'de-DE',
      dateModified: '2026-03-27',
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Datenschutzerklärung</h1>
          
          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <p className="text-gray-700 leading-relaxed">
              <strong>Stand:</strong> Januar 2025<br />
              <strong>Verantwortlicher:</strong> Future Now UG, Max-Planck-Str. 4, 85609 Aschheim
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Datenschutz auf einen Blick</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3">Allgemeine Hinweise</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
              passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
              persönlich identifiziert werden können.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Datenerfassung auf dieser Website</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
              können Sie dem Abschnitt „Hinweis zur verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Wie erfassen wir Ihre Daten?</strong><br />
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich 
              z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben. Andere Daten werden automatisch 
              oder nach Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Hosting</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir hosten die Inhalte unserer Website bei einem externen Dienstleister. Die personenbezogenen Daten, 
              die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert. Hierbei kann 
              es sich v. a. um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, 
              Kontaktdaten, Namen, Websitezugriffe und sonstige Daten, die über eine Website generiert werden, handeln.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Allgemeine Hinweise und Pflichtinformationen</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3">Datenschutz</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln 
              Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzbestimmungen 
              sowie dieser Datenschutzerklärung.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Hinweis zur verantwortlichen Stelle</h3>
            <div className="bg-teal-50 p-4 rounded-lg mb-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>Future Now UG</strong><br />
                Max-Planck-Str. 4<br />
                85609 Aschheim<br />
                Deutschland<br />
                <strong>Geschäftsführer:</strong> Olaf Niggemann
              </p>
            </div>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Speicherdauer</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, 
              verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Datenerfassung auf dieser Website</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3">Server-Log-Dateien</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, 
              die Ihr Browser automatisch an uns übermittelt. Dies sind:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Browsertyp und Browserversion</li>
              <li>verwendetes Betriebssystem</li>
              <li>Referrer URL</li>
              <li>Hostname des zugreifenden Rechners</li>
              <li>Uhrzeit der Serveranfrage</li>
              <li>IP-Adresse</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Kontaktformular</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
              Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung 
              der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Telegram Bot</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Unser Mindshift AI Service wird über Telegram bereitgestellt. Wenn Sie unseren Telegram Bot nutzen:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Werden Ihre Nachrichten verschlüsselt übertragen</li>
              <li>Speichern wir keine persönlichen Identifikationsdaten</li>
              <li>Erfolgt die Kommunikation anonym</li>
              <li>Werden Gesprächsdaten nicht dauerhaft gespeichert</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Ihre Rechte</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sie haben jederzeit das Recht:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten</li>
              <li>Berichtigung oder Löschung dieser Daten zu verlangen</li>
              <li>eine Einschränkung der Datenverarbeitung zu verlangen</li>
              <li>der Datenverarbeitung zu widersprechen</li>
              <li>sich bei einer Aufsichtsbehörde zu beschweren</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Widerspruch gegen Werbe-E-Mails</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten zur Übersendung von 
              nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit widersprochen.
            </p>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
